import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaSignOutAlt, FaChevronDown, FaChevronUp, FaHome, FaUser, FaCopy, FaCheck, FaQrcode } from 'react-icons/fa';
import cdePlusLogo from '../assets/images/cde_plus_logo.png';
import CustomAlert from './CustomAlert';
import CustomerInfoPopup from './CustomerInfoPopup';
import authService from '../services/authService';

const HeaderContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 1000;
  background: white;
  border-bottom: 2px solid ${props => props.theme.primary};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  width: 100%;
`;

const HeaderMain = styled.div`
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 60px;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Logo = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
  background: ${props => props.theme.primary};
  border-radius: 8px;
  padding: 6px;
`;

const CustomerName = styled.button`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: background 0.2s;
  
  &:hover {
    background: ${props => props.theme.surface};
  }
`;

const NameText = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.theme.primary};
`;

const CustomerNameDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const IconButton = styled.button`
  background: ${props => props.theme.primary};
  border: none;
  color: white;
  padding: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.85rem;

  &:hover {
    background: ${props => props.theme.primaryDark};
  }
`;

const DetailsDropdown = styled.div<{ isOpen: boolean }>`
  max-height: ${props => props.isOpen ? '300px' : '0'};
  overflow: ${props => props.isOpen ? 'auto' : 'hidden'};
  transition: max-height 0.3s ease;
  background: ${props => props.theme.surface};
  border-top: 1px solid ${props => props.theme.border};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const DetailsContent = styled.div`
  padding: 1rem;
  display: grid;
  grid-template-columns: repeat(2, minmax(150px, 1fr));
  column-gap: 1rem;
  row-gap: 0.75rem;
  min-height: fit-content;
  max-width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    column-gap: 0.75rem;
    padding: 0.75rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, minmax(120px, 1fr));
    column-gap: 0.5rem;
    row-gap: 0.5rem;
    padding: 0.5rem;
  }
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem;
  background: ${props => props.theme.background};
  border-radius: 8px;
  border: 1px solid ${props => props.theme.border};
  min-width: 0;
  width: 100%;
  
  @media (max-width: 480px) {
    padding: 0.4rem;
    gap: 0.15rem;
    border-radius: 6px;
  }
`;

const DetailLabel = styled.span`
  font-size: 0.75rem;
  color: ${props => props.theme.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
    letter-spacing: 0.3px;
  }
`;

const DetailValue = styled.span`
  font-size: 0.9rem;
  color: ${props => props.theme.textPrimary};
  font-weight: 500;
  word-break: break-word;

  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const CopyableField = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const CopyButton = styled.button<{ copied?: boolean }>`
  background: ${props => props.copied ? props.theme.success : props.theme.primary};
  color: white;
  border: none;
  padding: 0.4rem 0.6rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 480px) {
    padding: 0.3rem 0.5rem;
    font-size: 0.7rem;
  }
`;

const ChangeCustomerButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  margin-top: 0.75rem;
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover {
    background: ${props => props.theme.primaryDark};
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

interface CustomerData {
  name: string;
  code?: string;
  number?: string;
  aliasName?: string;
  type?: string;
  address?: string;
  city?: string;
  province?: string;
  mobile?: string;
}

interface SalesData {
  uid: string;
  loginId: string;
  empNo: string;
  name: string;
  email: string;
  contactNumber: string;
  roleName: string;
  orgName: string;
  orgUID: string;
}

interface CustomerHeaderProps {
  showHomeButton?: boolean;
  showScannerButton?: boolean;
  skipAuthCheck?: boolean;
  hideCustomerData?: boolean;
}

const CustomerHeader: React.FC<CustomerHeaderProps> = ({ showHomeButton = true, showScannerButton = false, skipAuthCheck = false, hideCustomerData = false }) => {
  const navigate = useNavigate();
  const [customerData, setCustomerData] = useState<CustomerData | null>(null);
  const [salesData, setSalesData] = useState<SalesData | null>(null);
  const [userType, setUserType] = useState<'customer' | 'sales'>('customer');
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [alert, setAlert] = useState<{
    isVisible: boolean;
    type?: 'success' | 'error' | 'warning' | 'info' | 'confirm';
    title?: string;
    message: string;
    onConfirm?: () => void;
  }>({ isVisible: false, message: '' });
  const [customerInfoPopup, setCustomerInfoPopup] = useState<{
    isVisible: boolean;
    initialCode: string;
    initialName: string;
  }>({ isVisible: false, initialCode: '', initialName: '' });

  useEffect(() => {
    // Skip authentication check for public pages but still load customer data
    if (skipAuthCheck) {
      // If hideCustomerData is true, don't load any customer data
      if (hideCustomerData) {
        setCustomerData({
          name: 'Guest',
          code: ''
        });
        return;
      }

      const customerInfo = authService.getCustomerData();
      if (customerInfo) {
        setCustomerData(customerInfo);
      } else {
        setCustomerData({
          name: 'Guest',
          code: 'REGISTRATION'
        });
      }
      return;
    }

    const storedUserType = authService.getUserType();
    setUserType(storedUserType as 'customer' | 'sales');

    if (storedUserType === 'sales') {
      const salesInfo = authService.getSalesData();
      if (salesInfo) {
        setSalesData(salesInfo);
      }
      // Removed navigate('/') redirect
    } else {
      const customerInfo = authService.getCustomerData();
      const storedGreenTag = localStorage.getItem('greenTagNumber');

      if (customerInfo) {
        setCustomerData(customerInfo);
      } else if (storedGreenTag) {
        setCustomerData({
          name: 'Customer',
          code: storedGreenTag
        });
      }
      // Removed navigate('/') redirect
    }
  }, [navigate, skipAuthCheck, hideCustomerData]);

  // Close dropdown on scroll
  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;
    
    const handleScroll = (e: Event) => {
      // Ignore scroll events from the dropdown itself
      const target = e.target as HTMLElement;
      if (target && target.closest && target.closest('[data-dropdown-content]')) {
        return;
      }
      
      if (isDetailsOpen) {
        // Use timeout to prevent closing while clicking to open
        scrollTimeout = setTimeout(() => {
          setIsDetailsOpen(false);
        }, 50);
      }
    };

    // Add scroll listener when dropdown is open with a small delay
    // This prevents the dropdown from closing immediately when opened
    if (isDetailsOpen) {
      const addListenerTimeout = setTimeout(() => {
        window.addEventListener('scroll', handleScroll, true);
        // Also listen to touch move for mobile
        window.addEventListener('touchmove', handleScroll, true);
      }, 200); // Delay to allow dropdown animation to start
      
      // Cleanup function
      return () => {
        clearTimeout(addListenerTimeout);
        clearTimeout(scrollTimeout);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('touchmove', handleScroll, true);
      };
    }
  }, [isDetailsOpen]);

  const handleLogout = () => {
    setAlert({
      isVisible: true,
      type: 'confirm',
      title: 'Logout',
      message: 'Are you sure you want to logout?',
      onConfirm: () => {
        authService.logout();
        navigate('/');
      }
    });
  };

  const handleHome = () => {
    navigate('/dashboard');
  };

  const handleChangeCustomer = () => {
    setIsDetailsOpen(false);
    
    // Get current customer info
    const storedCustomerData = localStorage.getItem('customerData');
    let customerCode = '';
    let customerName = '';

    if (storedCustomerData) {
      try {
        const parsedData = JSON.parse(storedCustomerData);
        customerCode = parsedData.code || parsedData.Code || parsedData.number || parsedData.Number || '';
        customerName = parsedData.name || parsedData.Name || '';
      } catch (e) {
        customerCode = localStorage.getItem('customerCode') || '';
        customerName = localStorage.getItem('customerName') || '';
      }
    } else {
      customerCode = localStorage.getItem('customerCode') || '';
      customerName = localStorage.getItem('customerName') || '';
    }

    setCustomerInfoPopup({
      isVisible: true,
      initialCode: customerCode,
      initialName: customerName
    });
  };

  const handleSaveCustomerInfo = (code: string, name: string) => {
    // Update localStorage
    localStorage.setItem('customerCode', code);
    localStorage.setItem('customerName', name);

    // Update customerData object in localStorage
    const existingCustomerData = localStorage.getItem('customerData');
    let customerData: any = {};

    if (existingCustomerData) {
      try {
        customerData = JSON.parse(existingCustomerData);
      } catch (e) {
        customerData = {};
      }
    }

    customerData.code = code;
    customerData.Code = code;
    customerData.name = name;
    customerData.Name = name;

    localStorage.setItem('customerData', JSON.stringify(customerData));

    // Update state
    setCustomerData({
      ...customerData,
      name: name,
      code: code
    });

    // Close popup
    setCustomerInfoPopup({ isVisible: false, initialCode: '', initialName: '' });

    // Show success message
    setAlert({
      isVisible: true,
      type: 'success',
      title: 'Success',
      message: 'Customer information updated successfully!'
    });

    // Reload the page to reflect changes
    window.location.reload();
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }).catch(() => {
      setAlert({
        isVisible: true,
        type: 'error',
        title: 'Copy Failed',
        message: 'Failed to copy customer code to clipboard.'
      });
    });
  };

  // Simplified public header for registration/verification forms
  if (skipAuthCheck) {
    const displayName = customerData?.name || 'Guest';

    return (
      <>
        <HeaderContainer>
          <HeaderMain>
            <LeftSection>
              <Logo src={cdePlusLogo} alt="CDE+ Service Portal" />
              {hideCustomerData ? (
                <CustomerNameDisplay>
                  <NameText>{displayName}</NameText>
                </CustomerNameDisplay>
              ) : (
                <CustomerName onClick={handleChangeCustomer}>
                  <NameText>{displayName}</NameText>
                </CustomerName>
              )}
            </LeftSection>
            <RightSection>
              {showScannerButton && (
                <IconButton onClick={() => navigate('/')} style={{ background: 'transparent', color: '#333', padding: '0.4rem' }}>
                  {React.createElement(FaQrcode as any, { size: 22 })}
                </IconButton>
              )}
              {showHomeButton && (
                <IconButton onClick={() => navigate('/dashboard')}>
                  {React.createElement(FaHome as any, {})} Home
                </IconButton>
              )}
            </RightSection>
          </HeaderMain>
        </HeaderContainer>

        <CustomAlert
          isVisible={alert.isVisible}
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={() => setAlert({ isVisible: false, message: '' })}
          onConfirm={alert.onConfirm}
        />

        {!hideCustomerData && (
          <CustomerInfoPopup
            isVisible={customerInfoPopup.isVisible}
            initialCode={customerInfoPopup.initialCode}
            initialName={customerInfoPopup.initialName}
            onSave={handleSaveCustomerInfo}
            onClose={() => setCustomerInfoPopup({ isVisible: false, initialCode: '', initialName: '' })}
          />
        )}
      </>
    );
  }

  // If no customer or sales data, show basic header
  if (!customerData && !salesData) {
    return (
      <>
        <HeaderContainer>
          <HeaderMain>
            <LeftSection>
              <Logo src={cdePlusLogo} alt="CDE+ Service Portal" />
              {hideCustomerData ? (
                <CustomerNameDisplay>
                  <NameText>CDE Customer Services</NameText>
                </CustomerNameDisplay>
              ) : (
                <CustomerName onClick={handleChangeCustomer}>
                  <NameText>CDE Customer Services</NameText>
                </CustomerName>
              )}
            </LeftSection>
            <RightSection>
              {showScannerButton && (
                <IconButton onClick={() => navigate('/')} style={{ background: 'transparent', color: '#333', padding: '0.4rem' }}>
                  {React.createElement(FaQrcode as any, { size: 22 })}
                </IconButton>
              )}
              {showHomeButton && (
                <IconButton onClick={() => navigate('/dashboard')}>
                  {React.createElement(FaHome as any, {})} Home
                </IconButton>
              )}
            </RightSection>
          </HeaderMain>
        </HeaderContainer>

        <CustomAlert
          isVisible={alert.isVisible}
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={() => setAlert({ isVisible: false, message: '' })}
          onConfirm={alert.onConfirm}
        />

        {!hideCustomerData && (
          <CustomerInfoPopup
            isVisible={customerInfoPopup.isVisible}
            initialCode={customerInfoPopup.initialCode}
            initialName={customerInfoPopup.initialName}
            onSave={handleSaveCustomerInfo}
            onClose={() => setCustomerInfoPopup({ isVisible: false, initialCode: '', initialName: '' })}
          />
        )}
      </>
    );
  }

  const displayName = userType === 'sales' && salesData
    ? salesData.name
    : customerData?.name || 'Customer';

  return (
    <>
      <HeaderContainer>
        <HeaderMain>
          <LeftSection>
            <Logo src={cdePlusLogo} alt="CDE+ Service Portal" />
            <CustomerName onClick={() => setIsDetailsOpen(!isDetailsOpen)}>
              {userType === 'sales' && React.createElement(FaUser as any, { size: 14, color: "#e31837", style: { marginRight: '4px' } })}
              <NameText>{displayName}</NameText>
              {isDetailsOpen ? (
                React.createElement(FaChevronUp as any, { size: 14, color: "#666" })
              ) : (
                React.createElement(FaChevronDown as any, { size: 14, color: "#666" })
              )}
            </CustomerName>
          </LeftSection>
          <RightSection>
            {showScannerButton && (
              <IconButton onClick={() => navigate('/')}>
                {React.createElement(FaQrcode as any, {})} Scan
              </IconButton>
            )}
            {showHomeButton && (
              <IconButton onClick={handleHome}>
                {React.createElement(FaHome as any, {})} Home
              </IconButton>
            )}
            <IconButton onClick={handleLogout}>
              {React.createElement(FaSignOutAlt as any, {})} Logout
            </IconButton>
          </RightSection>
        </HeaderMain>
        <DetailsDropdown isOpen={isDetailsOpen}>
          <DetailsContent data-dropdown-content>
            {userType === 'sales' && salesData ? (
              <>
                <DetailItem>
                  <DetailLabel>Employee No</DetailLabel>
                  <DetailValue>{salesData.empNo}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Email</DetailLabel>
                  <DetailValue>{salesData.email}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Role</DetailLabel>
                  <DetailValue>{salesData.roleName}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Organization</DetailLabel>
                  <DetailValue>{salesData.orgName}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Login ID</DetailLabel>
                  <DetailValue>{salesData.loginId}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Contact</DetailLabel>
                  <DetailValue>{salesData.contactNumber || 'N/A'}</DetailValue>
                </DetailItem>
              </>
            ) : customerData ? (
              <>
                <DetailItem style={{ gridColumn: 'span 2' }}>
                  <DetailLabel>Customer Code</DetailLabel>
                  <CopyableField>
                    <DetailValue>{customerData.code || customerData.number || 'N/A'}</DetailValue>
                    {(customerData.code || customerData.number) && (
                      <CopyButton
                        copied={copiedCode}
                        onClick={() => handleCopyCode(customerData.code || customerData.number || '')}
                      >
                        {copiedCode ? (
                          <>
                            {React.createElement(FaCheck as any, { size: 12 })}
                            Copied
                          </>
                        ) : (
                          <>
                            {React.createElement(FaCopy as any, { size: 12 })}
                            Copy
                          </>
                        )}
                      </CopyButton>
                    )}
                  </CopyableField>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Store</DetailLabel>
                  <DetailValue>{customerData.aliasName || 'N/A'}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>CDE Type</DetailLabel>
                  <DetailValue>{customerData.type || 'N/A'}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Location</DetailLabel>
                  <DetailValue>
                    {customerData.city && customerData.province
                      ? `${customerData.city}, ${customerData.province}`
                      : 'N/A'}
                  </DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Contact</DetailLabel>
                  <DetailValue>{customerData.mobile || 'N/A'}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Address</DetailLabel>
                  <DetailValue>{customerData.address || 'N/A'}</DetailValue>
                </DetailItem>
                <ChangeCustomerButton onClick={handleChangeCustomer}>
                  Change Customer Info
                </ChangeCustomerButton>
              </>
            ) : null}
          </DetailsContent>
        </DetailsDropdown>
      </HeaderContainer>

      <CustomAlert
        isVisible={alert.isVisible}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert({ isVisible: false, message: '' })}
        onConfirm={alert.onConfirm}
      />

      <CustomerInfoPopup
        isVisible={customerInfoPopup.isVisible}
        initialCode={customerInfoPopup.initialCode}
        initialName={customerInfoPopup.initialName}
        onSave={handleSaveCustomerInfo}
        onClose={() => setCustomerInfoPopup({ isVisible: false, initialCode: '', initialName: '' })}
      />
    </>
  );
};

export default CustomerHeader;